import { Row, UseRowSelectInstanceProps } from 'react-table'
import { Icon } from '@amzn/storm-ui'
import React, { Dispatch, SetStateAction, useState } from 'react'
import {
  BulkEditHeaderDivider,
  BulkEditHeaderText,
  ChildrenContainer,
  ClearButton,
  StyledBulkEditContainer,
  AdvertisersModalText,
} from './bulk_unlink_header_v2.styles'
import { FlexContainer, InviteModal } from '@amzn/aapn-react-components'
import {
  AdvertiserEntityType,
  GetAdvertisersQuery,
  GetAdvertisersQueryVariables,
  PartnerAdvertiserEdge,
  RemovePartnerAdvertiserMutation,
  useRemovePartnerAdvertiserMutation,
} from '../../../../../../generated/graphql'
import { getMetricsPublisherAsync, publishCounter } from '@amzn/aapn-front-end-metrics'
import { Trans, useTranslation } from 'react-i18next'
import { ApolloQueryResult, FetchResult } from '@apollo/client'
import { PARTNER_ID_HEADER_KEY } from '../../../../../../util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { toNullable } from 'fp-ts/es6/Option'
import * as stringKey from '../manage_advertisers_v2_i18n_mappings'
import { useLoginContext } from 'stagedive/LoginContext'
import { AdvertisersDeleteHeader } from '../advertisers_delete_modal'
import { UnlinkAdvertisersResult } from '../bulk_manage'
interface BulkEditCustomProps {
  setAdvertisers: Dispatch<SetStateAction<PartnerAdvertiserEdge[]>>
  setCursor: Dispatch<SetStateAction<string | null>>
  setPageNum: Dispatch<SetStateAction<number>>
  partnerCompanyName: string
  refetchAdvertisers: (variables?: GetAdvertisersQueryVariables) => Promise<ApolloQueryResult<GetAdvertisersQuery>>
  setUnlinkRequestResult: Dispatch<SetStateAction<UnlinkAdvertisersResult>>
  unlinkRequestResult: UnlinkAdvertisersResult
}

type BulkEditProps = React.PropsWithChildren<UseRowSelectInstanceProps<PartnerAdvertiserEdge> & BulkEditCustomProps>

export const BulkEditHeaderV2: React.FunctionComponent<BulkEditProps> = (props: BulkEditProps): JSX.Element => {
  const {
    selectedFlatRows,
    toggleAllRowsSelected,
    setAdvertisers,
    setCursor,
    setPageNum,
    partnerCompanyName,
    refetchAdvertisers,
    children,
    setUnlinkRequestResult,
    unlinkRequestResult,
  } = props
  const { t } = useTranslation()
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''
  const [isLoading, setIsLoading] = useState(false)
  const [deleteMutationFn] = useRemovePartnerAdvertiserMutation()

  const removePartnerAdvertiser = (row: Row<PartnerAdvertiserEdge>): Promise<FetchResult<RemovePartnerAdvertiserMutation>> =>
    deleteMutationFn({
      variables: {
        input: {
          advertiserEntity: {
            advertiserId: row.original.node.id,
            advertiserEntityType: row.original.node.type as AdvertiserEntityType,
          },
        },
      },
      context: {
        headers: {
          [PARTNER_ID_HEADER_KEY]: partnerAccountId,
        },
        linkOptions: {
          timeoutLink: {
            enableTimeout: false,
          },
        },
      },
    })

  const onClickDelete = (): void => {
    const unlinkPromises: Promise<FetchResult<RemovePartnerAdvertiserMutation>>[] = []
    const originalUnlinkIds = selectedFlatRows.map((row: Row<PartnerAdvertiserEdge>) => row.original.node.id)
    setIsLoading(true)
    setUnlinkRequestResult(null)
    selectedFlatRows.forEach((row: Row<PartnerAdvertiserEdge>) => {
      unlinkPromises.push(
        removePartnerAdvertiser(row).catch((error) => {
          getMetricsPublisherAsync('BulkUnlinkPartnerAdvertiserV2').then((p) => {
            publishCounter('unlinkTransaction.individual.fail', 1)(p)
          })
          return error
        }),
      )
    })

    Promise.all(unlinkPromises).then((unlinkedAdvertisers: FetchResult<RemovePartnerAdvertiserMutation>[]) => {
      const unlinkedAdvertiserIds = new Set(unlinkedAdvertisers.map((fetchedResult) => fetchedResult.data?.removePartnerAdvertiser?.id).filter((id) => !!id))
      const unlinkFailedAdvertiserIds = originalUnlinkIds.filter((id) => !unlinkedAdvertiserIds.has(id))

      // If some of the unlink ops failed, we should stay on the same page. Otherwise, go to pg 1 to avoid edge cases.
      if (unlinkFailedAdvertiserIds.length === 0) {
        setUnlinkRequestResult('success')
        refetchAdvertisers()
          .then(() => {
            setPageNum(0)
            setCursor(null)
          })
          .catch(() => {
            // Remove unlinked rows only after toggling otherwise we get React unmounted component error
            setAdvertisers((prev) => prev.filter((currentAdvertiser: PartnerAdvertiserEdge) => !unlinkedAdvertiserIds.has(currentAdvertiser?.node?.id)))
          })
          .finally(() => {
            getMetricsPublisherAsync('BulkUnlinkPartnerAdvertiserV2').then((p) => {
              publishCounter('unlinkTransaction.individual.success', unlinkedAdvertiserIds.size)(p)
            })
            setIsLoading(false)
          })
      } else {
        getMetricsPublisherAsync('BulkUnlinkPartnerAdvertiserV2').then((p) => {
          publishCounter('unlinkTransaction.fail.overall', 1)(p)
        })
        setUnlinkRequestResult('error')
        refetchAdvertisers().then(() => {
          setIsLoading(false)
        })
      }
    })
  }
  return (
    <StyledBulkEditContainer>
      <FlexContainer>
        <BulkEditHeaderText>{t(stringKey.ADVERTISERS_BULK_UNLINK_SELECTED_COUNT, { numberOfAdvertisers: selectedFlatRows.length })}</BulkEditHeaderText>
        <BulkEditHeaderDivider textColor={'tertiary'}>{'|'}</BulkEditHeaderDivider>
        <BulkEditHeaderText textColor={'tertiary'}>{t(stringKey.ADVERTISERS_BULK_UNLINK_HEADER)}</BulkEditHeaderText>
        <ChildrenContainer>{children}</ChildrenContainer>
        <InviteModal
          cancelLabel={t(stringKey.ADVERTISERS_BULK_UNLINK_CANCEL_LABEL)}
          inviteLabel={t(stringKey.ADVERTISERS_BULK_UNLINK_SEND_LABEL)}
          loadingLabel={t(stringKey.ADVERTISERS_BULK_UNLINK_LOADING_LABEL)}
          sendLabel={t(stringKey.ADVERTISERS_BULK_UNLINK_SEND_LABEL)}
          isLoading={isLoading}
          isSuccess={!!unlinkRequestResult}
          secondary={true}
          onSetData={onClickDelete}
          header={<AdvertisersDeleteHeader headerLabel={t(stringKey.ADVERTISERS_BULK_UNLINK_MODAL_HEADER)} />}
        >
          <AdvertisersModalText>
            <Trans
              i18nKey={stringKey.ADVERTISERS_BULK_UNLINK_MESSAGE_LABEL}
              values={{ numAdvertisers: selectedFlatRows.length, partnerCompanyName }}
              components={[<strong key={'key'} />]}
            />
          </AdvertisersModalText>
        </InviteModal>
      </FlexContainer>
      <ClearButton onClick={() => toggleAllRowsSelected(false)} aria-label={t(stringKey.ADVERTISERS_BULK_UNLINK_CLEAR_LABEL)}>
        <Icon type={'times'} size={'1x'} />
      </ClearButton>
    </StyledBulkEditContainer>
  )
}
